/** SUNTRONIX ONE — Quote Admin Console.
 *  Suntronix commercial workspace to review, price, discount, and
 *  approve incoming catalogue quotes.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, FileText, Percent, IndianRupee, RefreshCw } from "lucide-react";
import { customerPartNumber } from "@/lib/partNumber";
import { ProtectionOptions } from "@/components/ProtectionOptions";

const fmtINR = (n) => (n == null ? "—" :
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR",
    maximumFractionDigits: 0 }).format(n));

const STATUS_TONE = {
  SUBMITTED: "bg-amber-100 text-amber-800 border-amber-200",
  UNDER_REVIEW: "bg-blue-100 text-blue-800 border-blue-200",
  APPROVED: "bg-emerald-100 text-emerald-800 border-emerald-200",
  CONVERTED: "bg-slate-100 text-slate-700 border-slate-200",
  REJECTED: "bg-rose-100 text-rose-800 border-rose-200",
};


export default function QuoteAdmin() {
  const [quotes, setQuotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [adj, setAdj] = useState({});          // { product_code: new_price }
  const [discountPct, setDiscountPct] = useState(0);
  const [notes, setNotes] = useState("");
  const [approving, setApproving] = useState(false);
  const [protPlans, setProtPlans] = useState({});   // { product_code: selected EHS plan }
  const [statusFilter, setStatusFilter] = useState("SUBMITTED");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/catalogue-cart/quotes");
      setQuotes(data.items || []);
    } catch (e) { toast.error("Failed to load quotes"); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = (q) => {
    setSelected(q);
    setAdj({});
    setDiscountPct(q.discount_pct || 0);
    setNotes(q.approval_notes || "");
  };

  const filtered = statusFilter === "all" ? quotes
    : quotes.filter((q) => q.status === statusFilter);
  const kpi = {
    total: quotes.length,
    submitted: quotes.filter((q) => q.status === "SUBMITTED").length,
    approved: quotes.filter((q) => q.status === "APPROVED").length,
    converted: quotes.filter((q) => q.status === "CONVERTED").length,
    pipeline_inr: quotes
      .filter((q) => ["SUBMITTED", "UNDER_REVIEW", "APPROVED"].includes(q.status))
      .reduce((s, q) => s + (q.grand_total_inr || q.subtotal_inr || 0), 0),
  };

  const computeLive = () => {
    if (!selected) return { subtotal: 0, discount: 0, total: 0 };
    let subtotal = 0;
    for (const it of (selected.items || [])) {
      const price = adj[it.product_code] != null
        ? Number(adj[it.product_code]) : (it.list_price_inr || 0);
      subtotal += price * (it.quantity || 0);
    }
    const discount = subtotal * (Number(discountPct || 0) / 100);
    return { subtotal, discount, total: subtotal - discount };
  };

  const approve = async () => {
    if (!selected) return;
    setApproving(true);
    try {
      const payload = { notes: notes || null };
      const lineAdjustments = {};
      for (const [k, v] of Object.entries(adj)) {
        if (v !== "" && v != null) lineAdjustments[k] = Number(v);
      }
      if (Object.keys(lineAdjustments).length) payload.line_adjustments = lineAdjustments;
      if (Number(discountPct) > 0) payload.discount_pct = Number(discountPct);
      const { data } = await api.post(
        `/catalogue-cart/quotes/${selected.id}/approve`, payload);
      toast.success(`Approved — grand total ${fmtINR(data.grand_total_inr)}`);
      load();
      setSelected(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally { setApproving(false); }
  };

  const live = computeLive();

  return (
    <div className="space-y-4 max-w-[1600px]" data-testid="quote-admin-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">
            Commercial · Quote Admin
          </div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900 tracking-tight mt-1 flex items-center gap-2">
            <FileText className="h-6 w-6 text-slate-700" strokeWidth={1.6} />
            Catalogue Quotes
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Review incoming cart-to-quote requests, adjust line prices, apply discount, and approve.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={busy}
          data-testid="quote-admin-refresh">
          <RefreshCw className={"h-4 w-4 mr-1.5 " + (busy ? "animate-spin" : "")} />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label="Total quotes" value={kpi.total} testId="kpi-total" />
        <Kpi label="Submitted" value={kpi.submitted} tone="warn" testId="kpi-submitted" />
        <Kpi label="Approved" value={kpi.approved} tone="ok" testId="kpi-approved" />
        <Kpi label="Converted" value={kpi.converted} testId="kpi-converted" />
        <Kpi label="Pipeline" value={fmtINR(kpi.pipeline_inr)} testId="kpi-pipeline" />
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-3 flex gap-2 items-center">
        {["all", "SUBMITTED", "APPROVED", "CONVERTED"].map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)}
            data-testid={`quote-filter-${s}`}
            className={"h-8 px-3 rounded text-[12px] font-medium " +
              (statusFilter === s
                ? "bg-slate-900 text-white"
                : "text-slate-600 hover:bg-slate-100")}>
            {s === "all" ? "All" : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
        <span className="ml-auto text-[11px] text-slate-500">{filtered.length} row(s)</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-md divide-y">
        {filtered.length === 0 && (
          <div className="p-8 text-center text-[13px] text-slate-500">No quotes.</div>
        )}
        {filtered.map((q) => (
          <button key={q.id} onClick={() => openDetail(q)}
            data-testid={`quote-row-${q.id}`}
            className="w-full text-left p-4 hover:bg-slate-50 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[13px] font-medium">{q.code}</span>
                <Badge className={"text-[10px] " + (STATUS_TONE[q.status] || "")}>{q.status}</Badge>
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {q.requested_by_name || q.requested_by} · {q.items?.length || 0} item(s)
                {q.delivery_country ? ` · ${q.delivery_country}` : ""}
                · {new Date(q.created_at).toLocaleDateString()}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500">Total</div>
              <div className="text-[15px] font-heading font-semibold text-slate-900">
                {fmtINR(q.grand_total_inr || q.subtotal_inr)}
              </div>
            </div>
          </button>
        ))}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end"
          onClick={() => setSelected(null)} data-testid="quote-drawer">
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-white h-full overflow-auto p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500">
                  {selected.code}
                </div>
                <h2 className="text-2xl font-heading font-semibold text-slate-900 mt-1">
                  {selected.requested_by_name}
                </h2>
                <div className="text-[12px] text-slate-500 mt-0.5">
                  {selected.requested_by} · {selected.master_company_id?.slice(0, 8)}
                  <Badge className={"ml-2 text-[10px] " + (STATUS_TONE[selected.status] || "")}>{selected.status}</Badge>
                </div>
              </div>
              <button onClick={() => setSelected(null)}
                className="text-slate-500 hover:text-slate-900 text-[13px]"
                data-testid="quote-drawer-close">Close ✕</button>
            </div>

            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">
                Line items · price adjustments
              </div>
              <div className="border border-slate-200 rounded-md divide-y">
                {(selected.items || []).map((it) => {
                  const currentPrice = adj[it.product_code] != null
                    ? adj[it.product_code] : (it.list_price_inr || 0);
                  const lineTotal = Number(currentPrice) * (it.quantity || 0);
                  const editable = ["SUBMITTED", "UNDER_REVIEW"].includes(selected.status);
                  const prot = protPlans[it.product_code];
                  return (
                    <div key={it.product_code} className="p-3"
                      data-testid={`quote-line-${it.product_code}`}>
                     <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium text-slate-900 truncate">{it.name}</div>
                        <div className="text-[11px] text-slate-500 font-mono">
                          {customerPartNumber(it)} · qty {it.quantity}
                          {it.entitlement_status === "NOT_ENTITLED" && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                              Not in contract
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="w-32">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400">Unit price</div>
                        {editable ? (
                          <Input type="number" value={currentPrice}
                            onChange={(e) => setAdj((s) => ({ ...s, [it.product_code]: e.target.value }))}
                            className="h-8 text-[12px] font-mono"
                            data-testid={`quote-price-${it.product_code}`} />
                        ) : (
                          <div className="text-[12px] font-mono text-slate-800 mt-0.5">
                            {fmtINR(it.list_price_inr)}
                          </div>
                        )}
                      </div>
                      <div className="w-32 text-right">
                        <div className="text-[9px] uppercase tracking-wider text-slate-400">Line total</div>
                        <div className="text-[13px] font-mono text-slate-900 mt-1">{fmtINR(lineTotal)}</div>
                      </div>
                     </div>
                      <ProtectionOptions productCode={it.product_code} quantity={it.quantity || 1}
                        onSelectPlan={async (plan) => {
                          try {
                            const { data } = await api.post(
                              `/catalogue-cart/quotes/${selected.id}/protection`,
                              { base_product_code: it.product_code,
                                plan_part_number: plan.part_number, quantity: it.quantity || 1 });
                            setProtPlans((s) => ({ ...s, [it.product_code]: plan }));
                            setSelected((q) => ({ ...q, items: data.items, subtotal_inr: data.subtotal_inr }));
                            toast.success(`Microsoft EHS Plus ${plan.total_years}yr added to quote`);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Could not add plan");
                          }
                        }} />
                      {prot && (
                        <div className="mt-1.5 ml-1 text-[12px] text-emerald-700 flex items-center gap-1.5"
                          data-testid={`protection-added-${it.product_code}`}>
                          ✓ EHS Plus {prot.total_years}yr ({prot.part_number}) · qty {prot.quantity} ·
                          <span className="font-mono">{fmtINR((prot.price || 0) * (prot.quantity || 1))}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500 mb-1">
                  Discount %
                </div>
                <div className="flex items-center gap-2">
                  <Percent className="h-4 w-4 text-slate-400" />
                  <Input type="number" min="0" max="60" value={discountPct}
                    onChange={(e) => setDiscountPct(e.target.value)}
                    className="h-9" placeholder="0"
                    data-testid="quote-discount-pct"
                    disabled={!["SUBMITTED", "UNDER_REVIEW"].includes(selected.status)} />
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.1em] text-slate-500 mb-1">
                  Notes for customer
                </div>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={2} placeholder="Any commercial notes…"
                  data-testid="quote-notes" />
              </div>
            </div>

            <div className="mt-5 border-t pt-3 space-y-1">
              <div className="flex justify-between text-[13px]">
                <span className="text-slate-500">Subtotal</span>
                <span className="font-mono">{fmtINR(live.subtotal)}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-slate-500">Discount ({discountPct || 0}%)</span>
                <span className="font-mono text-rose-700">−{fmtINR(live.discount)}</span>
              </div>
              <div className="flex justify-between text-[16px] font-semibold pt-1 border-t">
                <span>Grand total</span>
                <span className="font-mono text-slate-900" data-testid="quote-grand-total">
                  {fmtINR(live.total)}
                </span>
              </div>
            </div>

            {["SUBMITTED", "UNDER_REVIEW"].includes(selected.status) ? (
              <div className="mt-5 flex gap-2">
                <Button variant="outline" onClick={() => setSelected(null)}
                  className="flex-1 h-10">Cancel</Button>
                <Button
                  onClick={approve} disabled={approving}
                  className="flex-1 h-10 bg-emerald-700 hover:bg-emerald-800 text-white"
                  data-testid="quote-approve">
                  <CheckCircle2 className="h-4 w-4 mr-1.5" />
                  {approving ? "Approving…" : "Approve quote"}
                </Button>
              </div>
            ) : (
              <div className="mt-5 text-center text-[12px] text-slate-500">
                Quote is {selected.status}. No further approval actions.
              </div>
            )}

            {(selected.approvals_trail || []).length > 0 && (
              <div className="mt-6 border-t pt-4">
                <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 mb-2">
                  Approvals trail
                </div>
                <div className="space-y-2" data-testid="quote-approvals-trail">
                  {(selected.approvals_trail || []).map((t, i) => (
                    <div key={i} className="border border-slate-200 rounded-md p-2.5 bg-slate-50"
                      data-testid={`quote-trail-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">
                          {t.action}
                        </span>
                        <span className="text-[10px] text-slate-500 font-mono">
                          {t.at ? new Date(t.at).toLocaleString() : ""}
                        </span>
                      </div>
                      <div className="text-[11px] text-slate-600 mt-1">
                        <span className="font-medium">{t.by}</span>
                        {t.discount_pct != null && t.discount_pct > 0 && (
                          <span> · Discount {t.discount_pct}%</span>
                        )}
                        {t.grand_total_inr != null && (
                          <span> · Total {fmtINR(t.grand_total_inr)}</span>
                        )}
                        {t.request_code && (
                          <span> · Order {t.request_code}</span>
                        )}
                      </div>
                      {t.notes && (
                        <div className="text-[11px] text-slate-500 mt-1 italic">"{t.notes}"</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function Kpi({ label, value, tone, testId }) {
  const tint = tone === "ok" ? "text-emerald-700"
             : tone === "warn" ? "text-amber-700"
             : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={"text-2xl font-heading font-semibold mt-1 " + tint}>{value ?? "—"}</div>
    </div>
  );
}
